import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ImageWithBasePath from "../imageWithBasePath";
import { fetchUpcomingBirthdays, BirthdayPerson } from "./birthdayApi";
import "./BirthdaysWidget.css";

const CONFETTI_COLORS = [
  "#F26D6D",
  "#F2A24B",
  "#F6CB52",
  "#58C18E",
  "#2BB6A8",
  "#4AA3E0",
  "#8B7BE0",
  "#E07BC0",
];

const LOGO_DOTS = [
  "#F26D6D",
  "#F2A24B",
  "#F6CB52",
  "#58C18E",
  "#2BB6A8",
  "#4AA3E0",
];

const AVATAR_PALETTE = [
  { grad: "linear-gradient(135deg,#149C8E,#2BB6A8)", ring: "#149C8E" },
  { grad: "linear-gradient(135deg,#4AA3E0,#3E78C9)", ring: "#4AA3E0" },
  { grad: "linear-gradient(135deg,#8B7BE0,#6B5BD0)", ring: "#8B7BE0" },
  { grad: "linear-gradient(135deg,#E07BC0,#D45BA8)", ring: "#E07BC0" },
  { grad: "linear-gradient(135deg,#F2A24B,#E0853B)", ring: "#F2A24B" },
];

const colorFor = (id: number) =>
  AVATAR_PALETTE[Math.abs(id) % AVATAR_PALETTE.length];

const initialsOf = (name: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const daysSuffix = (d: number) => (d <= 0 ? "today" : `${d}d`);

const yearsLabel = (y: number) => `${y} yr${y === 1 ? "" : "s"}`;

const Avatar = ({ person }: { person: BirthdayPerson }) => {
  const c = colorFor(person.id);
  return (
    <div
      className="bcard-avatar"
      style={{ ["--ring" as any]: c.ring } as React.CSSProperties}
    >
      {person.profile_pic ? (
        <ImageWithBasePath src={person.profile_pic} alt={person.name} />
      ) : (
        <div className="bcard-ini" style={{ backgroundImage: c.grad }}>
          {initialsOf(person.name)}
        </div>
      )}
    </div>
  );
};

const Bcard = ({ person }: { person: BirthdayPerson }) => (
  <div className={`bcard${person.isToday ? " is-today" : ""}`}>
    <div className="bcard-top">
      <Avatar person={person} />
      <div className="bcard-info">
        <p className="bcard-name">{person.name}</p>
        <p className="bcard-meta">{person.designation || "—"}</p>
      </div>
      {typeof person.years === "number" && (
        <span className="bcard-years" title={`${yearsLabel(person.years)} at the company`}>
          <i className="ti ti-award" />
          {person.years}
        </span>
      )}
    </div>
    <div className="bcard-date">
      <i className="ti ti-calendar-event" />
      <span className="bcard-date-txt">{person.date}</span>
      <span className="bcard-date-d">· {daysSuffix(person.daysUntil)}</span>
    </div>
  </div>
);

const CARD_STEP = 226; // 215 card + 11 gap

const BirthdayCarousel = ({ people }: { people: BirthdayPerson[] }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const pausedRef = useRef(false);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const startXRef = useRef(0);
  const startPosRef = useRef(0);
  const lastXRef = useRef(0); // previous pointer X for velocity calc
  const velocityRef = useRef(0); // momentum after drag
  const tweenRef = useRef<{
    total: number;
    elapsed: number;
    traveled: number;
  } | null>(null);

  const [loop, setLoop] = useState(false);
  const loopRef = useRef(false);

  const reduced = useMemo(
    () =>
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false,
    [],
  );

  const wrap = () => {
    const track = trackRef.current;
    if (!track || !loopRef.current) return;
    const half = track.scrollWidth / 2;
    if (half <= 0) return;
    if (posRef.current >= half) posRef.current -= half;
    else if (posRef.current < 0) posRef.current += half;
  };

  const applyPos = () => {
    const track = trackRef.current;
    if (track) track.scrollLeft = posRef.current;
  };

  useEffect(() => {
    loopRef.current = loop;
  }, [loop]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const gap = 11;
      const children = Array.from(track.children) as HTMLElement[];
      const n = Math.min(people.length, children.length);
      let setWidth = 0;
      for (let i = 0; i < n; i++) setWidth += children[i].offsetWidth;
      if (n > 1) setWidth += gap * (n - 1);
      const need = setWidth > track.clientWidth + 1;
      setLoop((prev) => (prev === need ? prev : need));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people.length]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    posRef.current = 0;
    track.scrollLeft = 0;
    velocityRef.current = 0;

    const SPEED = 0.03; // px per ms — gentle auto-scroll
    const FRICTION = 0.92; // momentum decay per frame
    let raf = 0;
    let last = 0;

    const loop = (t: number) => {
      if (!last) last = t;
      let dt = t - last;
      last = t;

      if (dt > 64) dt = 16;

      if (draggingRef.current) {
        raf = requestAnimationFrame(loop);
        return;
      }

      // Tween (arrow navigation)
      if (tweenRef.current) {
        const tw = tweenRef.current;
        tw.elapsed += dt;
        const p = Math.min(tw.elapsed / 500, 1);
        // Smooth ease-in-out (sine)
        const eased = 0.5 - 0.5 * Math.cos(Math.PI * p);
        const target = tw.total * eased;
        posRef.current += target - tw.traveled;
        tw.traveled = target;
        if (p >= 1) tweenRef.current = null;
        wrap();
        applyPos();
      }
      // Momentum after drag release
      else if (Math.abs(velocityRef.current) > 0.2) {
        posRef.current += velocityRef.current;
        velocityRef.current *= FRICTION;
        if (Math.abs(velocityRef.current) < 0.2) velocityRef.current = 0;
        wrap();
        applyPos();
      }
      // Auto-scroll
      else if (!reduced && !pausedRef.current) {
        if (track.scrollWidth / 2 > track.clientWidth) {
          posRef.current += SPEED * dt;
          wrap();
          applyPos();
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onWheel = (e: WheelEvent) => {
      if (!loopRef.current) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        posRef.current += e.deltaX * 0.6; // dampen wheel speed
        wrap();
        applyPos();
      }
    };
    track.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      cancelAnimationFrame(raf);
      track.removeEventListener("wheel", onWheel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced, people.length, loop]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!loopRef.current) return;
    draggingRef.current = true;
    movedRef.current = false;
    startXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    startPosRef.current = posRef.current;
    velocityRef.current = 0; // reset momentum so the loop doesn't fight
    tweenRef.current = null;
    trackRef.current?.classList.add("dragging");
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startXRef.current;
    if (Math.abs(dx) > 3) movedRef.current = true;
    const moveDelta = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    posRef.current = startPosRef.current - dx;
    velocityRef.current = -moveDelta;
    wrap();
    applyPos();
  };

  const endDrag = (e: React.PointerEvent) => {
    draggingRef.current = false;
    const maxV = 18;
    if (velocityRef.current > maxV) velocityRef.current = maxV;
    if (velocityRef.current < -maxV) velocityRef.current = -maxV;
    trackRef.current?.classList.remove("dragging");
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (movedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      movedRef.current = false;
    }
  };

  const goArrow = (dir: number) => {
    const dist = dir * CARD_STEP * 1.5;
    if (reduced) {
      posRef.current += dist;
      wrap();
      applyPos();
      return;
    }
    tweenRef.current = { total: dist, elapsed: 0, traveled: 0 };
  };

  const items = loop ? [...people, ...people] : people;

  return (
    <div
      className="bcarousel"
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      <div
        className="bcarousel-track"
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
      >
        {items.map((p, i) => (
          <Bcard key={`${p.id}-${i}`} person={p} />
        ))}
      </div>

      {loop && (
        <>
          <div className="bcarousel-fade left" aria-hidden="true" />
          <div className="bcarousel-fade right" aria-hidden="true" />

          <button
            type="button"
            className="bcarousel-arrow left"
            aria-label="Previous"
            onClick={() => goArrow(-1)}
          >
            <i className="ti ti-chevron-left" />
          </button>
          <button
            type="button"
            className="bcarousel-arrow right"
            aria-label="Next"
            onClick={() => goArrow(1)}
          >
            <i className="ti ti-chevron-right" />
          </button>
        </>
      )}
    </div>
  );
};

const CelebrationSection = ({
  icon,
  title,
  noun,
  people,
  wide,
}: {
  icon: string;
  title: string;
  noun: string;
  people: BirthdayPerson[];
  wide?: boolean;
}) => {
  const today = useMemo(() => people.filter((p) => p.isToday), [people]);
  const upcoming = useMemo(() => people.filter((p) => !p.isToday), [people]);

  const [tab, setTab] = useState<"today" | "upcoming">("today");

  // Default to whichever tab actually has people (prefer Today) as data loads.
  useEffect(() => {
    setTab(today.length > 0 ? "today" : "upcoming");
  }, [today.length, upcoming.length]);

  const active = tab === "today" ? today : upcoming;
  const emptyText =
    tab === "today" ? `No ${noun} today.` : `No upcoming ${noun}.`;

  return (
    <div className={`bday-pane ${wide ? "bday-pane-wide" : "bday-pane-narrow"}`}>
      <div className="bday-pane-head">
        <div className="bday-label">
          <i className={`ti ${icon}`} />
          <span className="bday-label-text">{title}</span>
        </div>
        <div className="bday-tabs" role="tablist" aria-label={title}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "today"}
            className={`bday-tab${tab === "today" ? " is-active" : ""}`}
            onClick={() => setTab("today")}
          >
            Today
            <span className="bday-tab-count">{today.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "upcoming"}
            className={`bday-tab${tab === "upcoming" ? " is-active" : ""}`}
            onClick={() => setTab("upcoming")}
          >
            Upcoming
            <span className="bday-tab-count">{upcoming.length}</span>
          </button>
        </div>
      </div>

      {active.length > 0 ? (
        <BirthdayCarousel key={tab} people={active} />
      ) : (
        <div className="bday-empty">
          <i className={`ti ${icon}`} />
          <span>{emptyText}</span>
        </div>
      )}
    </div>
  );
};

const BirthdaysWidget = () => {
  const [birthdays, setBirthdays] = useState<BirthdayPerson[]>([]);
  const [anniversaries, setAnniversaries] = useState<BirthdayPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchUpcomingBirthdays()
      .then(({ all, anniversaries: anns }) => {
        if (!mounted) return;
        setBirthdays(all);
        setAnniversaries(anns);
      })
      .catch((err) => {
        console.error("[birthday] widget load failed", err);
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const confetti = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 4 + Math.random() * 4,
        rot: -60 + Math.random() * 120,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      })),
    [],
  );

  return (
    <div className="col-12 d-flex">
      <div className="card flex-fill bday-card">
        <div className="bday-head">
          <div className="bday-confetti" aria-hidden="true">
            {confetti.map((c, i) => (
              <span
                key={i}
                style={{
                  left: `${c.left}%`,
                  top: `${c.top}%`,
                  width: c.size,
                  height: c.size,
                  background: c.color,
                  transform: `rotate(${c.rot}deg)`,
                }}
              />
            ))}
          </div>

          <div className="bday-head-tile">
            <i className="ti ti-cake" />
          </div>
          <div className="bday-head-titles">
            <h5 className="bday-head-title">Celebrations</h5>
            <p className="bday-head-sub">Birthdays &amp; work anniversaries</p>
          </div>
          <div className="bday-logo-dots" aria-hidden="true">
            {LOGO_DOTS.map((c, i) => (
              <span key={i} style={{ background: c }} />
            ))}
          </div>
        </div>

        <div className="bday-body">
          {loading ? (
            <div className="bday-empty">
              <i className="ti ti-loader" />
              <span>Loading birthdays…</span>
            </div>
          ) : error ? (
            <div className="bday-empty">
              <i className="ti ti-alert-circle" />
              <span>Couldn't load birthdays.</span>
            </div>
          ) : (
            <div className="bday-split">
              <CelebrationSection
                icon="ti-cake"
                title="Birthdays"
                noun="birthdays"
                people={birthdays}
                wide
              />

              <div className="bday-vdivider" aria-hidden="true" />

              <CelebrationSection
                icon="ti-award"
                title="Work Anniversaries"
                noun="work anniversaries"
                people={anniversaries}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BirthdaysWidget;
